import React from "react";
import { Link } from "react-router-dom";
import "./LegalPages.css";

const TermsOfServicePage = () => {
  return (
    <main className="legal-page">
      <div className="legal-shell">
        <header className="legal-hero">
          <p className="legal-kicker">Legal</p>
          <h1>Terms of Service</h1>
          <p className="legal-meta">
            Last Updated: May 16, 2026. These Terms of Service govern your access
            to and use of TattQR products, websites, applications, QR content
            management tools, and related services.
          </p>
        </header>

        <div className="legal-intro-grid">
          <section className="legal-panel">
            <h2>What These Terms Cover</h2>
            <p>
              These terms describe the legal agreement between you and TattQR,
              including account responsibilities, acceptable use, intellectual
              property, payment obligations, and dispute rules.
            </p>
          </section>
          <section className="legal-panel">
            <h2>Quick Navigation</h2>
            <ol className="legal-toc">
              <li><a href="#acceptance">Acceptance of Terms</a></li>
              <li><a href="#eligibility">Eligibility and Accounts</a></li>
              <li><a href="#services">Services and Availability</a></li>
              <li><a href="#content">User Content and License</a></li>
              <li><a href="#acceptable-use">Acceptable Use</a></li>
              <li><a href="#billing">Billing and Payments</a></li>
              <li><a href="#ip">Intellectual Property</a></li>
              <li><a href="#liability">Disclaimers and Liability</a></li>
              <li><a href="#termination">Suspension and Termination</a></li>
              <li><a href="#law">Governing Law and Contact</a></li>
            </ol>
          </section>
        </div>

        <div className="legal-content">
          <section className="legal-section" id="acceptance">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, clicking to accept, purchasing a service, or
              using TattQR, you agree to be bound by these Terms and our{" "}
              <Link className="legal-inline-link" to="/privacy-policy">
                Privacy Policy
              </Link>
              . If you use TattQR on behalf of a company or organization, you
              represent that you have authority to bind that entity.
            </p>
          </section>

          <section className="legal-section" id="eligibility">
            <h2>2. Eligibility and Accounts</h2>
            <ul>
              <li>You must be at least 18 years old to create and manage an account.</li>
              <li>
                You are responsible for maintaining the confidentiality of account
                credentials and for all activity under your account.
              </li>
              <li>
                You must provide accurate and current information and promptly update
                it when it changes.
              </li>
              <li>
                You must immediately notify us at{" "}
                <a className="legal-inline-link" href="mailto:tattqr@gmail.com">
                  tattqr@gmail.com
                </a>{" "}
                if you suspect unauthorized access.
              </li>
            </ul>
          </section>

          <section className="legal-section" id="services">
            <h2>3. Services and Availability</h2>
            <p>
              TattQR provides tools to create, host, and update scannable QR-linked
              experiences and digital profiles. Service features may change over time
              to improve reliability, safety, or performance.
            </p>
            <p>
              We aim for high availability, but we do not guarantee uninterrupted
              access. Scheduled maintenance, technical incidents, third-party outages,
              or force majeure events may impact service continuity.
            </p>
          </section>

          <section className="legal-section" id="content">
            <h2>4. User Content and License</h2>
            <p>
              You retain ownership of text, images, links, and other materials you
              upload or publish through TattQR ("User Content"). You grant TattQR a
              worldwide, non-exclusive, royalty-free license to host, process,
              reproduce, and display User Content solely to operate and improve the
              service.
            </p>
            <p>
              You represent that you own or have the rights needed to submit User
              Content, and that the content does not violate laws or third-party
              rights.
            </p>
          </section>

          <section className="legal-section" id="acceptable-use">
            <h2>5. Acceptable Use</h2>
            <p>You agree not to use TattQR to:</p>
            <ul>
              <li>Distribute malware, phishing links, or malicious code.</li>
              <li>
                Publish unlawful, defamatory, fraudulent, or infringing content.
              </li>
              <li>
                Attempt to reverse engineer, scrape, overload, or bypass service
                security controls.
              </li>
              <li>
                Use automated systems to access the platform in a manner that degrades
                service for other users.
              </li>
              <li>Impersonate others or misrepresent identity or affiliation.</li>
            </ul>
          </section>

          <section className="legal-section" id="billing">
            <h2>6. Billing and Payments</h2>
            <p>
              Paid plans and services, if offered, are billed under the pricing and
              billing terms shown at purchase. Unless otherwise stated, fees are
              non-refundable after service delivery begins.
            </p>
            <ul>
              <li>You authorize us to charge your selected payment method.</li>
              <li>
                Taxes, duties, or local fees are your responsibility unless stated
                otherwise.
              </li>
              <li>
                We may suspend paid features for failed or overdue payments after
                reasonable notice.
              </li>
            </ul>
          </section>

          <section className="legal-section" id="ip">
            <h2>7. Intellectual Property</h2>
            <p>
              TattQR software, design systems, source code, trademarks, and related
              service materials are owned by TattQR or its licensors and protected by
              applicable intellectual property laws.
            </p>
            <p>
              Except where expressly permitted in writing, you may not copy, modify,
              distribute, sublicense, or create derivative works from TattQR platform
              materials.
            </p>
          </section>

          <section className="legal-section" id="liability">
            <h2>8. Disclaimers and Limitation of Liability</h2>
            <p>
              TattQR is provided on an "as is" and "as available" basis. To the
              maximum extent permitted by law, we disclaim implied warranties
              including merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>
            <p>
              To the fullest extent permitted by law, TattQR and its affiliates are
              not liable for indirect, incidental, special, consequential, or punitive
              damages, or for loss of profits, data, goodwill, or business
              interruption arising from your use of the service.
            </p>
            <div className="legal-note">
              In jurisdictions that do not allow certain limitations, liability is
              limited to the minimum extent required by applicable law.
            </div>
          </section>

          <section className="legal-section" id="termination">
            <h2>9. Suspension and Termination</h2>
            <p>
              We may suspend or terminate access if you violate these Terms, create
              material security or legal risk, or fail to pay applicable fees.
            </p>
            <p>
              You may stop using TattQR at any time. Provisions that by nature should
              survive termination (including payment obligations, liability limits, and
              dispute provisions) will remain in effect.
            </p>
          </section>

          <section className="legal-section" id="law">
            <h2>10. Governing Law, Changes, and Contact</h2>
            <p>
              These Terms are governed by the laws of the State of California, without
              regard to conflict-of-law principles. We may update these Terms from time
              to time. If changes are material, we will provide reasonable notice in
              the product or by email.
            </p>
            <p>
              Questions about these Terms can be sent to{" "}
              <a className="legal-inline-link" href="mailto:tattqr@gmail.com">
                tattqr@gmail.com
              </a>{" "}
              or by mail to TattQR, California, United States.
            </p>
          </section>
        </div>

        <div className="legal-actions">
          <Link className="legal-action-link" to="/privacy-policy">
            View Privacy Policy
          </Link>
          <Link className="legal-action-link" to="/landing">
            Back to Landing
          </Link>
        </div>
      </div>
    </main>
  );
};

export default TermsOfServicePage;
